import React from 'react';
import { Button, Tag, Divider } from 'antd';
import {
  BranchesOutlined,
  FileTextOutlined,
  WhatsAppOutlined,
  MailOutlined,
  CheckCircleOutlined,
  NodeIndexOutlined,
  AppstoreAddOutlined,
} from '@ant-design/icons';
import { useLanguage } from '../../../contexts/LanguageContext';

const CHANNEL_TAGS = {
  whatsapp: { color: 'green', icon: <WhatsAppOutlined />, label: 'WhatsApp' },
  email: { color: 'blue', icon: <MailOutlined />, label: 'Email' },
  manual: { color: 'orange', icon: <CheckCircleOutlined />, label: '手動任務' },
};

const TYPE_COLOR = {
  workflow: '#7234CF',
  eform: '#1677ff',
  metaflow: '#6d28d9',
  'workflow+eform': '#7234CF',
  'workflow+metaflow': '#7234CF',
};

// Normalise whatever the AI returned into a recognised planType key.
function normalisePlanType(raw) {
  if (!raw) return null;
  const s = raw.toLowerCase();
  if (s === 'workflow' || s === 'workflow+eform' || s === 'workflow+metaflow' || s === 'eform' || s === 'metaflow') return s;
  // Fuzzy match from free-form AI text
  if (s.includes('meta')) return 'metaflow';
  if (s.includes('eform') || s.includes('form') || s.includes('表單')) return 'eform';
  if (s.includes('workflow') || s.includes('流程')) return 'workflow';
  return null;
}

const label = (t, key, fallback) => {
  const v = t(key);
  return (!v || v === key) ? fallback : v;
};

const BuildAppPlanCard = ({ plan, onConfirm, onModify, confirming }) => {
  const { t } = useLanguage();
  if (!plan) return null;

  const { summary, channel = [], workflowNodes = [], eformFields = [], estimatedNodes } = plan;
  const planType = normalisePlanType(plan.planType) || plan.planType;
  const typeColor = TYPE_COLOR[planType] || '#fa8c16';

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <AppstoreAddOutlined style={{ color: typeColor, fontSize: 18 }} />
        <span style={styles.headerTitle}>{label(t, 'buildApp.planCardTitle', '建置計劃')}</span>
        <Tag color={typeColor} style={styles.typeTag}>{planType}</Tag>
      </div>

      <p style={styles.summary}>{summary}</p>

      {/* 渠道 */}
      {channel.length > 0 && (
        <div style={styles.section}>
          <span style={styles.sectionLabel}>{label(t, 'buildApp.planChannel', '渠道')}</span>
          <div style={styles.tagRow}>
            {channel.map((ch) => {
              const info = CHANNEL_TAGS[ch] || { color: 'default', label: ch };
              return (
                <Tag key={ch} color={info.color} icon={info.icon}>
                  {info.label}
                </Tag>
              );
            })}
          </div>
        </div>
      )}

      {/* 工作流程節點 */}
      {workflowNodes.length > 0 && (
        <div style={styles.section}>
          <span style={styles.sectionLabel}>
            <BranchesOutlined style={{ marginRight: 4 }} />
            {label(t, 'buildApp.planWorkflowNodes', '流程節點')}
            {estimatedNodes ? ` (${estimatedNodes})` : ''}
          </span>
          <div style={styles.nodeFlow}>
            {workflowNodes.map((n, i) => (
              <React.Fragment key={i}>
                <span style={styles.nodeChip}>{n}</span>
                {i < workflowNodes.length - 1 && <span style={styles.arrow}>→</span>}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* 表單欄位 */}
      {eformFields.length > 0 && (
        <div style={styles.section}>
          <span style={styles.sectionLabel}>
            <FileTextOutlined style={{ marginRight: 4 }} />
            {label(t, 'buildApp.planEformFields', '表單欄位')}
          </span>
          <div style={styles.tagRow}>
            {eformFields.map((f, i) => (
              <Tag key={i} color="default">{f}</Tag>
            ))}
          </div>
        </div>
      )}

      {/* Wizard 額外步驟 */}
      {(plan.needsTemplate || plan.needsDataset || plan.needsAiStaff || plan.needsKb) && (
        <div style={styles.section}>
          <span style={styles.sectionLabel}>{label(t, 'buildApp.planWizardSteps', '建置嚮導步驟')}</span>
          <div style={styles.tagRow}>
            {plan.needsTemplate && (
              <Tag color="cyan">{label(t, 'buildApp.wizardStep.template', '訊息模板')}{plan.templateType ? ` (${plan.templateType})` : ''}</Tag>
            )}
            {plan.needsDataset && (
              <Tag color="orange">{label(t, 'buildApp.wizardStep.dataset', 'Dataset')}</Tag>
            )}
            {plan.needsAiStaff && (
              <Tag color="magenta">{label(t, 'buildApp.wizardStep.aiStaff', 'AI 員工')}</Tag>
            )}
            {plan.needsKb && (
              <Tag color="green">{label(t, 'buildApp.wizardStep.kb', '知識庫')}</Tag>
            )}
          </div>
        </div>
      )}

      <Divider style={{ margin: '12px 0' }} />

      <div style={styles.actions}>
        <Button
          onClick={onModify}
          disabled={confirming}
          style={styles.modifyBtn}
        >
          {label(t, 'buildApp.planModify', '修改計劃')}
        </Button>
        <Button
          type="primary"
          icon={<NodeIndexOutlined />}
          loading={confirming}
          onClick={onConfirm}
          style={{ borderRadius: 8, fontWeight: 600 }}
        >
          {label(t, 'buildApp.planConfirm', '確認並開始生成')}
        </Button>
      </div>
    </div>
  );
};

const styles = {
  card: {
    background: '#faf5ff',
    border: '1.5px solid #d8b4fe',
    borderRadius: 12,
    padding: '16px 18px',
    maxWidth: 640,
    width: '100%',
    margin: '4px 0',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: 15,
    color: '#1a1a2e',
  },
  typeTag: {
    marginLeft: 'auto',
    fontWeight: 600,
    fontSize: 12,
  },
  summary: {
    fontSize: 14,
    color: '#444',
    margin: '0 0 12px 0',
    lineHeight: 1.6,
  },
  section: {
    marginBottom: 10,
  },
  sectionLabel: {
    display: 'block',
    fontSize: 12,
    color: '#8c8c8c',
    fontWeight: 600,
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  tagRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
  },
  nodeFlow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 4,
  },
  nodeChip: {
    background: '#fff',
    border: '1px solid #d9d9d9',
    borderRadius: 6,
    padding: '2px 8px',
    fontSize: 13,
    color: '#333',
  },
  arrow: {
    color: '#bbb',
    fontSize: 14,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
  },
  modifyBtn: {
    borderRadius: 8,
  },
};

export default BuildAppPlanCard;
